/** 일정 / 시간표 — 주간 타임테이블(요일×수업) + 학원 일정 추가/삭제 */
import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { IoAddCircle, IoTrashOutline, IoClose } from 'react-icons/io5';
import academyService from '../../services/academyService';
import { useConsoleAcademy } from '../../components/console/ConsoleLayout';
import {
  PageHead, PageTitle, PrimaryBtn, GhostBtn, Card, Empty, IconBtn,
  Overlay, Modal, ModalHead, ModalBody, ModalFoot, Field,
} from './consoleUi';

const DAYS = ['월', '화', '수', '목', '금', '토', '일'];

const ConsoleSchedulePage = () => {
  const { academy } = useConsoleAcademy();
  const aid = academy.id;
  const [loading, setLoading] = useState(true);
  const [classes, setClasses] = useState([]);
  const [events, setEvents] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ title: '', date: '', time: '', memo: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, e] = await Promise.all([
        academyService.getClasses(aid),
        academyService.getEvents(aid),
      ]);
      setClasses(c);
      setEvents(e);
    } finally {
      setLoading(false);
    }
  }, [aid]);

  useEffect(() => { load(); }, [load]);

  // 요일별 수업 세션 (시간순)
  const byDay = (d) => {
    const out = [];
    classes.forEach((c) => {
      (c.schedule || []).forEach((s) => {
        if (s.day === d) out.push({ id: `${c.id}-${d}-${s.time}`, name: c.name, time: s.time || '', color: c.color || '#4F46E5' });
      });
    });
    return out.sort((a, b) => (a.time || '').localeCompare(b.time || ''));
  };

  const addEvent = async () => {
    if (!form.title.trim() || !form.date) return;
    setSaving(true);
    try {
      await academyService.addEvent(aid, { ...form, title: form.title.trim() });
      setAdding(false);
      setForm({ title: '', date: '', time: '', memo: '' });
      await load();
    } finally {
      setSaving(false);
    }
  };

  const delEvent = async (ev) => {
    if (!window.confirm(`'${ev.title}' 일정을 삭제할까요?`)) return;
    await academyService.deleteEvent(aid, ev.id);
    await load();
  };

  return (
    <>
      <PageHead>
        <PageTitle>
          <h2>일정 / 시간표</h2>
          <p>주간 수업 시간표와 학원 일정을 한 화면에서 관리하세요.</p>
        </PageTitle>
        <PrimaryBtn onClick={() => setAdding(true)}><IoAddCircle size={18} /> 일정 추가</PrimaryBtn>
      </PageHead>

      <Card $pad="18px">
        <SectionTitle>주간 시간표</SectionTitle>
        {loading ? (
          <Empty>불러오는 중…</Empty>
        ) : (
          <Grid>
            {DAYS.map((d) => {
              const sessions = byDay(d);
              const weekend = d === '토' || d === '일';
              return (
                <Col key={d}>
                  <ColHead $weekend={weekend}>{d}</ColHead>
                  <ColBody>
                    {sessions.length === 0
                      ? <NoSession>-</NoSession>
                      : sessions.map((s) => (
                        <Block key={s.id} $c={s.color}>
                          <BTime>{s.time || '시간미정'}</BTime>
                          <BName>{s.name}</BName>
                        </Block>
                      ))}
                  </ColBody>
                </Col>
              );
            })}
          </Grid>
        )}
      </Card>

      <Card style={{ marginTop: 20 }}>
        <SectionTitle>학원 일정</SectionTitle>
        {loading ? (
          <Empty>불러오는 중…</Empty>
        ) : events.length === 0 ? (
          <Empty>등록된 일정이 없어요.</Empty>
        ) : (
          <EvList>
            {events.map((ev) => (
              <EvRow key={ev.id}>
                <EvDate>{ev.date}{ev.time ? ` ${ev.time}` : ''}</EvDate>
                <EvBody>
                  <EvTitle>{ev.title}</EvTitle>
                  {ev.memo && <EvMemo>{ev.memo}</EvMemo>}
                </EvBody>
                <IconBtn $danger onClick={() => delEvent(ev)}><IoTrashOutline size={18} /></IconBtn>
              </EvRow>
            ))}
          </EvList>
        )}
      </Card>

      {adding && (
        <Overlay onClick={() => !saving && setAdding(false)}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalHead>일정 추가 <IconBtn onClick={() => setAdding(false)}><IoClose size={18} /></IconBtn></ModalHead>
            <ModalBody>
              <Field>
                <span className="lab">제목</span>
                <input autoFocus value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              </Field>
              <Field>
                <span className="lab">날짜</span>
                <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
              </Field>
              <Field>
                <span className="lab">시간 (선택)</span>
                <input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} />
              </Field>
              <Field>
                <span className="lab">메모 (선택)</span>
                <textarea value={form.memo} onChange={(e) => setForm({ ...form, memo: e.target.value })} />
              </Field>
            </ModalBody>
            <ModalFoot>
              <GhostBtn onClick={() => setAdding(false)}>취소</GhostBtn>
              <PrimaryBtn onClick={addEvent} disabled={saving || !form.title.trim() || !form.date}>
                {saving ? '추가 중…' : '추가'}
              </PrimaryBtn>
            </ModalFoot>
          </Modal>
        </Overlay>
      )}
    </>
  );
};

export default ConsoleSchedulePage;

const SectionTitle = styled.h3` font-size: 15px; font-weight: 800; color: #1F2937; margin-bottom: 14px; `;

const Grid = styled.div` display: grid; grid-template-columns: repeat(7, 1fr); gap: 8px; `;
const Col = styled.div` border: 1px solid #EEF1F6; border-radius: 10px; overflow: hidden; `;
const ColHead = styled.div`
  text-align: center; padding: 9px 0; font-size: 13px; font-weight: 800;
  background: ${({ $weekend }) => ($weekend ? '#FFF5F5' : '#F4F6FB')};
  color: ${({ $weekend }) => ($weekend ? '#E11D48' : '#4338CA')};
`;
const ColBody = styled.div` padding: 8px; min-height: 120px; display: flex; flex-direction: column; gap: 7px; `;
const NoSession = styled.div` text-align: center; color: #D1D5DB; font-size: 13px; padding-top: 14px; `;
const Block = styled.div`
  background: ${({ $c }) => `${$c}12`}; border-left: 3px solid ${({ $c }) => $c};
  border-radius: 6px; padding: 7px 9px;
`;
const BTime = styled.div` font-size: 11px; font-weight: 700; color: #6B7280; `;
const BName = styled.div` font-size: 13px; font-weight: 700; color: #1F2937; margin-top: 2px; `;

const EvList = styled.div` display: flex; flex-direction: column; `;
const EvRow = styled.div`
  display: flex; align-items: center; gap: 16px; padding: 13px 4px;
  border-bottom: 1px solid #F1F3F7;
  &:last-child { border-bottom: none; }
`;
const EvDate = styled.div` font-size: 13px; font-weight: 700; color: #4A7CFE; width: 130px; flex-shrink: 0; `;
const EvBody = styled.div` flex: 1; `;
const EvTitle = styled.div` font-size: 14px; font-weight: 700; color: #1F2937; `;
const EvMemo = styled.div` font-size: 13px; color: #9CA3AF; margin-top: 2px; `;
